'use client'

import type { ComponentProps, ReactNode } from 'react'

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { ArrowLeftIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { DialogCloseButton } from '@/components/ui/dialog'

/*
 * GoodUnited / Sheet (drawer). Figma: 22 / Blocks / Drawers and 31 / Templates / Drawers.
 * Sheet -> SheetContent -> SheetHeader / SheetBody / SheetFooter.
 * Right side, full viewport height, widths sm 480, default 560, lg 720 (full width on phones).
 * side left is reserved for the AppSidebar slide-out menu on small screens.
 * Header and footer stay fixed; only SheetBody scrolls (24px padding). Focus stays inside,
 * Escape closes and focus returns to the trigger.
 */
type SheetSize = 'sm' | 'default' | 'lg'

const sizeClasses: Record<SheetSize, string> = {
    sm: 'sm:max-w-120',
    default: 'sm:max-w-140',
    lg: 'sm:max-w-180'
}

const Sheet = SheetPrimitive.Root

function SheetTrigger({ className, ...props }: Omit<SheetPrimitive.Trigger.Props, 'className'> & { className?: string }) {
    return (
        <SheetPrimitive.Trigger
            data-slot='sheet-trigger'
            className={className}
            {...props}
        />
    )
}

type SheetContentProps = Omit<SheetPrimitive.Popup.Props, 'className'> & {
    className?: string
    size?: SheetSize
    side?: 'right' | 'left'
    container?: SheetPrimitive.Portal.Props['container']
}

function SheetContent({ className, size = 'default', side = 'right', container, children, ...props }: SheetContentProps) {
    return (
        <SheetPrimitive.Portal container={container}>
            <SheetPrimitive.Backdrop className='fixed inset-0 z-50 bg-surface-scrim data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0' />
            <SheetPrimitive.Popup
                data-slot='sheet-content'
                data-size={size}
                data-side={side}
                className={cn(
                    'fixed inset-y-0 z-50 flex h-dvh w-full flex-col bg-surface-raised text-text-primary shadow-overlay outline-none data-open:animate-in data-closed:animate-out',
                    side === 'right'
                        ? cn('right-0 border-l border-border-default data-open:slide-in-from-right data-closed:slide-out-to-right', sizeClasses[size])
                        : 'left-0 max-w-80 data-open:slide-in-from-left data-closed:slide-out-to-left',
                    className
                )}
                {...props}
            >
                {children}
            </SheetPrimitive.Popup>
        </SheetPrimitive.Portal>
    )
}

interface SheetHeaderProps extends Omit<ComponentProps<'div'>, 'title'> {
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
    onBack?: () => void
    showClose?: boolean
}

/* SheetHeader: optional Back, Poppins / text-2xl / semibold title, description, actions and the round close. */
function SheetHeader({ title, description, actions, onBack, showClose = true, className, ...props }: SheetHeaderProps) {
    return (
        <div
            data-slot='sheet-header'
            className={cn('flex shrink-0 items-start gap-3 border-b border-border-subtle p-6', className)}
            {...props}
        >
            {onBack && (
                <button
                    type='button'
                    onClick={onBack}
                    aria-label='Back'
                    className='inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-action-secondary-border bg-action-secondary text-icon-primary transition-colors outline-none hover:bg-action-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus [&_svg]:size-4'
                >
                    <ArrowLeftIcon aria-hidden='true' />
                </button>
            )}
            <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <SheetPrimitive.Title className='font-heading text-2xl font-semibold text-text-primary'>
                    {title}
                </SheetPrimitive.Title>
                {description && (
                    <SheetPrimitive.Description className='text-sm text-text-secondary'>
                        {description}
                    </SheetPrimitive.Description>
                )}
            </div>
            {actions && (
                <div className='flex shrink-0 items-center gap-2'>
                    {actions}
                </div>
            )}
            {showClose && <DialogCloseButton />}
        </div>
    )
}

/* SheetBody: the only scrolling area. */
function SheetBody({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='sheet-body'
            className={cn('flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6', className)}
            {...props}
        />
    )
}

/* SheetFooter: fixed actions. Use Cancel + Save changes, a full-width Export data, or an Actions select. */
function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='sheet-footer'
            className={cn('flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle p-6', className)}
            {...props}
        />
    )
}

/* SheetSection: a titled group inside SheetBody (Poppins / text-lg / semibold). */
function SheetSection({ title, description, className, children, ...props }: Omit<ComponentProps<'section'>, 'title'> & { title?: ReactNode, description?: ReactNode }) {
    return (
        <section
            data-slot='sheet-section'
            className={cn('flex flex-col gap-4', className)}
            {...props}
        >
            {(title || description) && (
                <div className='flex flex-col gap-1'>
                    {title && (
                        <h3 className='font-heading text-lg font-semibold text-text-primary'>
                            {title}
                        </h3>
                    )}
                    {description && (
                        <p className='text-sm text-text-secondary'>
                            {description}
                        </p>
                    )}
                </div>
            )}
            {children}
        </section>
    )
}

const SheetClose = SheetPrimitive.Close

export {
    Sheet,
    SheetBody,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetSection,
    SheetTrigger
}
export type { SheetSize }
